import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, formatErr, timeAgo, plural } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";

export default function CommentsSheet({ post, open, onOpenChange, onAdded }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    if (open && post) api.get(`/posts/${post.id}/comments`).then((r) => setItems(r.data)).catch(() => {});
  }, [open, post]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { text });
      setItems((x) => [data, ...x]);
      setText("");
      onAdded?.();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto h-[70vh] max-w-xl rounded-t-md border-[#2A2E37] bg-[#121418] text-white" data-testid="comments-sheet">
        <SheetHeader><SheetTitle className="font-display text-white">{plural(items.length, "comment")}</SheetTitle></SheetHeader>
        <div className="mt-4 h-[calc(70vh-10rem)] space-y-4 overflow-y-auto pr-1" data-testid="comments-list">
          {items.length === 0 && <p className="text-sm text-gray-500">Be the first to say something.</p>}
          {items.map((c) => (
            <div key={c.id} className="flex gap-3 rise-in" data-testid="comment-item">
              <Avatar user={c.author} size="h-8 w-8" />
              <div>
                <p className="text-xs text-gray-400"><span className="font-semibold text-gray-200">@{c.author.username}</span> · {timeAgo(c.created_at)}</p>
                <p className="text-sm text-gray-100">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
        {user ? (
          <form onSubmit={submit} className="absolute bottom-4 left-4 right-4 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment..." data-testid="comment-input"
              className="flex-1 rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-2.5 text-sm outline-none focus:border-white/40" />
            <button type="submit" data-testid="comment-submit-btn" className="btn-neon rounded-sm p-3 text-white"><Send className="h-4 w-4" /></button>
          </form>
        ) : (
          <Link to="/login" data-testid="comment-login-link" className="absolute bottom-4 left-4 right-4 rounded-sm border border-[#2A2E37] py-3 text-center text-sm font-semibold">
            Log in to comment
          </Link>
        )}
      </SheetContent>
    </Sheet>
  );
}
